import { APIStatus } from "@/api/APIStatus.type";
import { MyEvolutionsEmptyState } from "@/components/organisms/PlayersList/MyEvolutionsEmptyState";
import { warningIcon } from "@/designSystemIcons";
import { trackExplore } from "@/events/evolutionsMixpanel";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { storeSignupContext } from "@/events/signupTracking";
import { addAlert } from "@/redux/message/messageSlice";
import { generateNavigationUrl } from "@/redux/players/playersSlice";
import {
  canUseClubImportSelector,
  userIsLoggedInSelector,
} from "@/redux/user/userSlice";
import { SKIP_MARK_AS_PLANNED_MODAL } from "@/settings";
import type { SavedEvolutionPlayer } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import {
  checkIfPlayerIsInEvolvedPlayersSelector,
  completeSavedPlayer,
  removeSavedPlayer,
  setSavedPlayerState,
} from "@repo/shared/redux";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { AppDispatch } from "../../../redux/store";
import { CompleteSavedEvoPopup } from "../Popups/CompleteSavedEvoPopup";
import { MarkAsPlannedPopup } from "../Popups/MarkAsPlannedPopup";
import { RemoveEvosPopup } from "../Popups/RemoveEvosPopup";
import { SavedEvoCard } from "./SavedEvoCard";

interface SavedEvoPlayersProps {
  savedEvolutionPlayers: SavedEvolutionPlayer[];
  savedEvolutionPlayersStatus: APIStatus;
}
export const SavedEvoPlayers = ({
  savedEvolutionPlayers,
  savedEvolutionPlayersStatus,
}: SavedEvoPlayersProps) => {
  const { t } = useTranslation(["evolutions", "common"]);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [showActionsForPlayer, setShowActionsForPlayer] = useState<
    string | null
  >(null);
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const [showRemovePopup, setShowRemovePopup] = useState(false);
  const [showCompletePopup, setShowCompletePopup] = useState(false);
  const [showMarkPlannedPopup, setShowMarkPlannedPopup] = useState(false);
  const playerIsInEvolvedPlayers = useSelector(
    checkIfPlayerIsInEvolvedPlayersSelector({
      savedPlayerId: showActionsForPlayer,
    }),
  );
  const canUseClubImport = useSelector(canUseClubImportSelector);
  const activePlayer = savedEvolutionPlayers.find(
    (p) => p.id === showActionsForPlayer,
  );

  const handleRemove = async () => {
    if (!showActionsForPlayer) return;
    try {
      await dispatch(removeSavedPlayer({ id: showActionsForPlayer }));
      mixpanelTrack("remove-planned-evolution", {
        id: showActionsForPlayer,
        resourceId: activePlayer?.player.resourceId,
        name: activePlayer?.player.name,
        evolutionChain: activePlayer?.player.evolutionData?.evolutionPath,
      });
      dispatch(
        addAlert({
          type: "success",
          description: t("myEvolutions.saved.alerts.removed"),
        }),
      );
    } catch {
      dispatch(
        addAlert({
          type: "error",
          description: t("myEvolutions.saved.alerts.removeFailed"),
        }),
      );
    }
    setShowActionsForPlayer(null);
  };

  const handleComplete = async () => {
    if (!showActionsForPlayer) return;
    try {
      await dispatch(completeSavedPlayer({ id: showActionsForPlayer }));
      mixpanelTrack("complete-planned-evolution", {
        id: showActionsForPlayer,
        resourceId: activePlayer?.player.resourceId,
        name: activePlayer?.player.name,
        evolutionChain: activePlayer?.player.evolutionData?.evolutionPath,
      });
      dispatch(
        addAlert({
          type: "success",
          description: t("myEvolutions.saved.alerts.completed"),
        }),
      );
    } catch {
      dispatch(
        addAlert({
          type: "error",
          description: t("myEvolutions.saved.alerts.completeFailed"),
        }),
      );
    }

    setShowActionsForPlayer(null);
  };

  const handleSetPlanned = async (
    planned: boolean,
    id: string | null = showActionsForPlayer,
  ) => {
    if (!id) return;
    try {
      await dispatch(
        setSavedPlayerState({
          id,
          state: planned ? "PLANNED" : null,
        }),
      ).unwrap();
      dispatch(
        addAlert({
          type: "success",
          description: t(
            planned
              ? "myEvolutions.saved.alerts.markedPlanned"
              : "myEvolutions.saved.alerts.unmarkedPlanned",
          ),
        }),
      );
    } catch {
      dispatch(
        addAlert({
          type: "error",
          description: t(
            planned
              ? "myEvolutions.saved.alerts.markPlannedFailed"
              : "myEvolutions.saved.alerts.unmarkPlannedFailed",
          ),
        }),
      );
    }
    setShowActionsForPlayer(null);
  };

  // Marking needs the confirm popup unless the user opted out of it.
  const handleMarkPlanned = (id: string) => {
    if (localStorage.getItem(SKIP_MARK_AS_PLANNED_MODAL) === "true") {
      handleSetPlanned(true, id);
      return;
    }
    setShowMarkPlannedPopup(true);
  };

  const handleClickSavedEvo = (savedPlayerId: string) => {
    const saved = savedEvolutionPlayers.find((p) => p.id === savedPlayerId);
    if (!saved) return;
    mixpanelTrack("resume-planned-evolution", {
      id: savedPlayerId,
      resourceId: saved.player.resourceId,
      name: saved.player.name,
      evolutionChain: saved.player.evolutionData?.evolutionPath,
    });
    const navLink = generateNavigationUrl({
      name: saved.player.name,
      resourceId: saved.player.resourceId,
      evoPathId: saved.player.evoPathId,
      evolutionPath: saved.player.evolutionData?.evolutionPath,
      pathwayPath: saved.player.evolutionData?.pathwayPath,
      basePath: "/evolutions/saved",
    });
    navigate(navLink);
  };

  if (
    !isLoggedIn ||
    savedEvolutionPlayersStatus === "rejected" ||
    savedEvolutionPlayers.length === 0
  ) {
    return (
      <MyEvolutionsEmptyState
        section="Evolutions"
        status={savedEvolutionPlayersStatus}
        title={t("myEvolutions.saved.emptyTitle")}
        subtitle={t("myEvolutions.saved.emptySubtitle")}
        cta={{
          label: t("myEvolutions.saved.emptyCta"),
          onClick: () => {
            trackExplore({
              section: "Evolutions",
              subsection: "Planned Evolutions",
              source: "ai evolution builder",
              destination_section: "Evolutions",
              destination_subsection: "Builder Overview",
            });
            navigate("/evolutions/builder");
          },
        }}
        onCreateAccountClick={() =>
          storeSignupContext("cta", {
            section: "Evolutions",
            subsection: "Planned Evolutions",
          })
        }
      />
    );
  }

  return (
    <>
      <div className="grid w-full grid-cols-1 gap-2 min-[560px]:grid-cols-2 min-[960px]:grid-cols-3 min-[1400px]:grid-cols-4">
        {savedEvolutionPlayers.map((saved) => (
          <SavedEvoCard
            key={saved.id}
            savedPlayer={saved}
            onRemove={() => {
              setShowActionsForPlayer(saved.id);
              setShowRemovePopup(true);
            }}
            onMarkCompleted={() => {
              setShowActionsForPlayer(saved.id);
              setShowCompletePopup(true);
            }}
            onMarkPlanned={() => {
              setShowActionsForPlayer(saved.id);
              handleMarkPlanned(saved.id);
            }}
            onUnmarkPlanned={() => handleSetPlanned(false, saved.id)}
            handleClickSavedEvo={handleClickSavedEvo}
          />
        ))}
      </div>

      {showRemovePopup && (
        <RemoveEvosPopup
          onClose={() => {
            setShowRemovePopup(false);
            setShowActionsForPlayer(null);
          }}
          onConfirm={() => {
            setShowRemovePopup(false);
            handleRemove();
          }}
        />
      )}

      {showMarkPlannedPopup && (
        <MarkAsPlannedPopup
          onClose={() => {
            setShowMarkPlannedPopup(false);
            setShowActionsForPlayer(null);
          }}
          onConfirm={() => {
            setShowMarkPlannedPopup(false);
            handleSetPlanned(true);
          }}
        />
      )}

      {showCompletePopup && (
        <CompleteSavedEvoPopup
          onClose={() => {
            setShowCompletePopup(false);
            setShowActionsForPlayer(null);
          }}
          warningMessage={
            playerIsInEvolvedPlayers && !canUseClubImport ? (
              <div className="flex flex-col gap-y-2 text-sm text-dark-tertiary">
                <div className="size-4 shrink-0 text-yellow-400">
                  {warningIcon}
                </div>
                <p>{t("myEvolutions.saved.overwriteWarning")}</p>
              </div>
            ) : undefined
          }
          ctaLabel={
            playerIsInEvolvedPlayers && !canUseClubImport
              ? t("common:overwrite")
              : t("common:confirm")
          }
          onConfirm={() => {
            setShowCompletePopup(false);
            handleComplete();
          }}
        />
      )}
    </>
  );
};
